import type { ComponentProps, PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { Block, ColoredBlock, OuterBlock, RegularBlock } from './card-block';
import { CardColumn } from './card-columns';
import { CardLayer } from './card-layer';

type ContainerProps = {
  _hasColor: boolean;
  _margin: number;
  _padding: number;
};

const Container = styled(motion.div)<ContainerProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  outline: none;
  overflow: hidden;

  ${Block}:not(:first-child) {
    margin-top: ${({ _margin }) => _margin}px;
  }

  ${Block}:only-child {
    flex: 1;
    max-height: 100%;
    max-width: 100%;
  }

  ${ColoredBlock} {
    ${({ _margin, _padding }) => css`
      padding: ${_margin}px ${_padding}px;
    `}
  }

  ${ColoredBlock} + ${ColoredBlock},
  ${OuterBlock} + ${OuterBlock} {
    margin-top: 0;
  }

  ${({ _hasColor, _padding }) =>
    _hasColor
      ? css`
          ${RegularBlock} {
            padding: 0 ${_padding}px;
          }
          ${RegularBlock}:first-child {
            padding-top: ${_padding}px;
          }
          ${RegularBlock}:last-child {
            padding-bottom: ${_padding}px;
          }

          ${ColoredBlock} + ${OuterBlock},
          ${OuterBlock} + ${ColoredBlock} {
            margin-top: 0;
          }
        `
      : css`
          ${CardColumn}:not(:first-child) {
            margin-left: ${_padding}px;
          }

          ${CardLayer} ${RegularBlock} {
            padding: 0 ${_padding}px;
          }
        `}
`;

export type CardContainerProps = Omit<
  ComponentProps<typeof Container>,
  keyof ContainerProps
> &
  PropsWithChildren<{
    backgroundColor?: string;
    margin?: number;
    padding?: number;
  }>;

export const CardContainer = forwardRef<HTMLDivElement, CardContainerProps>(
  function CardContainer(
    { backgroundColor, style, margin = 12, padding = 16, ...forwardingProps },
    forwardingRef
  ) {
    return (
      <Container
        _hasColor={!!backgroundColor && backgroundColor !== 'transparent'}
        _margin={margin}
        _padding={padding}
        style={{
          backgroundColor,
          ...style
        }}
        ref={forwardingRef}
        {...forwardingProps}
      />
    );
  }
);
